import { useEffect, useState } from "react";
import {
    blockNominationEntries,
    deleteNominationEntries,
    mergeNominationsBulk,
    type NominationEntry,
} from "../api/tcg";

function AdminMerge({
    token,
    categoryId,
    entries,
    onEntriesChanged,
}: {
    token: string;
    categoryId: number;
    entries: NominationEntry[];
    onEntriesChanged: () => Promise<void>;
}) {
    const [selectedFrom, setSelectedFrom] = useState<string[]>([]);
    const [selectedDelete, setSelectedDelete] = useState<string[]>([]);
    const [into, setInto] = useState("");
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => resetSelection(), [categoryId, entries]);

    function labelFor(norm: string): string {
        const e = entries.find((x) => x.normalized_text === norm);
        return e ? `${e.display_text} (${e.count})` : norm;
    }

    function resetSelection() {
        setSelectedFrom([]);
        setSelectedDelete([]);
        setInto("");
    }

    function toggleSelectedFrom(norm: string) {
        setMessage(null);
        setError(null);
        setSelectedFrom((current) =>
            current.includes(norm)
                ? current.filter((item) => item !== norm)
                : [...current, norm]
        );
    }

    function handleIntoChange(value: string) {
        setMessage(null);
        setError(null);
        setInto(value);
        setSelectedFrom((current) => current.filter((item) => item !== value));
    }

    async function handleBulkMerge() {
        const uniqueFrom = selectedFrom.filter((norm) => norm && norm !== into);
        if (uniqueFrom.length === 0 || !into) return;

        const sourceLabels = uniqueFrom.map(labelFor).join(", ");
        const targetLabel = labelFor(into);
        const confirmed = window.confirm(
            `Ausgewählte Einträge wirklich in „${targetLabel}" zusammenführen?\n\n` +
            `Verschwindet: ${sourceLabels}\n\n` +
            "Die Stimmen werden addiert und die ausgewählten Einträge verschwinden dauerhaft."
        );
        if (!confirmed) return;

        setBusy(true); setError(null); setMessage(null);
        try {
            await mergeNominationsBulk(token, categoryId, uniqueFrom, into);
            await onEntriesChanged();
            resetSelection();
            setMessage(`${uniqueFrom.length} Einträge zusammengeführt.`);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ausgewählte Einträge konnten nicht zusammengeführt werden.");
        } finally { setBusy(false); }
    }

    function toggleSelectedDelete(norm: string) {
        setMessage(null);
        setError(null);
        setSelectedDelete((current) =>
            current.includes(norm)
                ? current.filter((item) => item !== norm)
                : [...current, norm]
        );
    }

    function blockedCountText(blocked: number | string[] | undefined): string {
        if (Array.isArray(blocked)) return `${blocked.length} Begriffe wurden blockiert.`;
        if (typeof blocked === "number") return `${blocked} Begriffe wurden blockiert.`;
        return "";
    }

    // ── Löschen: Einträge verschwinden, dürfen aber neu nominiert werden ──
    async function handleDeleteOnly() {
        if (selectedDelete.length === 0) return;

        const sourceLabels = selectedDelete.map(labelFor).join(", ");
        const confirmed = window.confirm(
            "Ausgewählte Einträge und bekannte zusammengeführte Varianten wirklich löschen?\n\n" +
            `Auswahl: ${sourceLabels}\n\n` +
            "Die Begriffe werden NICHT blockiert und können erneut nominiert werden."
        );
        if (!confirmed) return;

        setBusy(true); setError(null); setMessage(null);
        try {
            await deleteNominationEntries(token, categoryId, selectedDelete, false);
            await onEntriesChanged();
            resetSelection();
            setMessage("Einträge wurden gelöscht.");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ausgewählte Einträge konnten nicht gelöscht werden.");
        } finally { setBusy(false); }
    }

    // ── Blockieren: Stimmen bleiben stehen, neue Nominierungen abgewiesen ──
    async function handleBlockOnly() {
        if (selectedDelete.length === 0) return;

        const sourceLabels = selectedDelete.map(labelFor).join(", ");
        const confirmed = window.confirm(
            "Ausgewählte Einträge und bekannte zusammengeführte Varianten wirklich blockieren?\n\n" +
            `Auswahl: ${sourceLabels}\n\n` +
            "Die vorhandenen Stimmen bleiben erhalten. Der Begriff kann künftig nicht mehr nominiert werden."
        );
        if (!confirmed) return;

        setBusy(true); setError(null); setMessage(null);
        try {
            const result = await blockNominationEntries(token, categoryId, selectedDelete);
            await onEntriesChanged();
            resetSelection();
            const skipped = result.alreadyBlocked > 0
                ? ` ${result.alreadyBlocked} waren bereits blockiert.`
                : "";
            setMessage(`${result.blocked.length} Begriffe wurden blockiert.${skipped}`);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ausgewählte Einträge konnten nicht blockiert werden.");
        } finally { setBusy(false); }
    }

    // ── Beides: löschen und dauerhaft sperren ────────────────────────────────
    async function handleDeleteAndBlock() {
        if (selectedDelete.length === 0) return;

        const sourceLabels = selectedDelete.map(labelFor).join(", ");
        const confirmed = window.confirm(
            "Ausgewählte Einträge und bekannte zusammengeführte Varianten wirklich löschen und künftig blockieren?\n\n" +
            `Auswahl: ${sourceLabels}`
        );
        if (!confirmed) return;

        setBusy(true); setError(null); setMessage(null);
        try {
            const result = await deleteNominationEntries(token, categoryId, selectedDelete, true);
            await onEntriesChanged();
            resetSelection();
            const blockedText = blockedCountText(result.blocked);
            setMessage(blockedText ? `Einträge wurden gelöscht. ${blockedText}` : "Einträge wurden gelöscht und blockiert.");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Ausgewählte Einträge konnten nicht gelöscht und blockiert werden.");
        } finally { setBusy(false); }
    }

    return (
        <>
            {message && <p className="admin-success">{message}</p>}
            {error && <p className="admin-error">{error}</p>}

            {/* Bulk-Zuordnung */}
            {entries.length > 0 && (
                <div className="admin-action-block">
                    <h3 className="admin-subtitle">Nominierungen zusammenführen</h3>
                    <p className="admin-note" style={{ marginBottom: "16px" }}>
                        Wähle ein Ziel aus und ordne mehrere ähnliche oder falsch geschriebene
                        Einträge gesammelt diesem Ziel zu.
                    </p>
                    <div className="admin-control-row" style={{ maxWidth: "none", marginBottom: "16px" }}>
                        <label className="admin-label">Richtig (Ziel)</label>
                        <select className="admin-input" value={into} onChange={(e) => handleIntoChange(e.target.value)}>
                            <option value="">– auswählen –</option>
                            {entries.map((e) => (
                                <option key={e.normalized_text} value={e.normalized_text}>
                                    {e.display_text} ({e.count})
                                </option>
                            ))}
                        </select>
                        <button className="admin-btn-primary" onClick={handleBulkMerge}
                            disabled={busy || !into || selectedFrom.filter((norm) => norm !== into).length === 0}>
                            Ausgewählte zusammenführen
                        </button>
                    </div>

                    <p className="admin-note" style={{ marginBottom: "12px" }}>
                        Wähle mehrere falsch geschriebene Einträge aus. Das Ziel kann nicht gleichzeitig
                        als falscher Eintrag ausgewählt werden.
                    </p>

                    <table className="admin-table">
                        <thead>
                            <tr><th>Auswahl</th><th>Eintrag</th><th>Anzahl</th></tr>
                        </thead>
                        <tbody>
                            {entries.map((e) => {
                                const isTarget = e.normalized_text === into;
                                return (
                                    <tr key={e.normalized_text}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedFrom.includes(e.normalized_text)}
                                                disabled={busy || isTarget}
                                                onChange={() => toggleSelectedFrom(e.normalized_text)}
                                                aria-label={`${e.display_text} auswählen`}
                                            />
                                        </td>
                                        <td>{e.display_text}</td>
                                        <td>{e.count}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Löschen und/oder blockieren */}
            {entries.length > 0 && (
                <div className="admin-action-block">
                    <h3 className="admin-subtitle">Einträge löschen / blockieren</h3>
                    <p className="admin-note" style={{ marginBottom: "12px" }}>
                        Wähle die betroffenen Einträge aus und entscheide dann, was passieren soll.
                        Bekannte zusammengeführte Varianten werden vom Backend automatisch mit berücksichtigt.
                    </p>

                    <ul className="admin-note" style={{ marginBottom: "16px", paddingLeft: "18px" }}>
                        <li><strong>Löschen</strong> – Einträge verschwinden, dürfen aber erneut nominiert werden.</li>
                        <li><strong>Blockieren</strong> – Stimmen bleiben sichtbar, neue Nominierungen werden abgewiesen.</li>
                        <li><strong>Löschen &amp; Blockieren</strong> – Einträge verschwinden und bleiben dauerhaft gesperrt.</li>
                    </ul>

                    <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                        <button className="admin-btn-secondary" onClick={handleDeleteOnly}
                            disabled={busy || selectedDelete.length === 0}>
                            Löschen
                        </button>
                        <button className="admin-btn-secondary" onClick={handleBlockOnly}
                            disabled={busy || selectedDelete.length === 0}>
                            Blockieren
                        </button>
                        <button className="admin-btn-primary" onClick={handleDeleteAndBlock}
                            disabled={busy || selectedDelete.length === 0}>
                            Löschen &amp; Blockieren
                        </button>
                    </div>

                    <table className="admin-table" style={{ marginTop: "12px" }}>
                        <thead>
                            <tr><th>Auswahl</th><th>Eintrag</th><th>Anzahl</th></tr>
                        </thead>
                        <tbody>
                            {entries.map((e) => (
                                <tr key={e.normalized_text}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedDelete.includes(e.normalized_text)}
                                            disabled={busy}
                                            onChange={() => toggleSelectedDelete(e.normalized_text)}
                                            aria-label={`${e.display_text} auswählen`}
                                        />
                                    </td>
                                    <td>{e.display_text}</td>
                                    <td>{e.count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

export default AdminMerge;
